import React, { ChangeEvent, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Theme,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { makeStyles } from '@material-ui/styles';

import FormSection from '../../../components/form/FormSection';
import ButtonRow from '../../../components/ButtonRow';
import Page from '../../../components/Page';
import {
  MinimalLessonLocation,
  Operator,
  SwimmingLessonProviderWithLessonLocations,
  User,
  UserDTO,
  ValidationViolation,
} from '../../../types';
import SwimmingLessonProviderSelect from '../../admin/forms/SwimmingLessonProviderSelect';
import OperatorSelect from '../../admin/forms/OperatorSelect';
import { USER_BASE_ROUTE } from '../index';
import UserRepository from '../repository/UserRepository';
import {
  hasViolationForField,
  isValidationErrorResponse,
} from '../../../utils/common';

const useStyles = makeStyles((theme: Theme) => ({
  field: {
    marginBottom: theme.spacing(2),
  },
}));

type SELECTABLE_ROLE =
  | 'ROLE_ADMIN'
  | 'ROLE_SWIMMING_LESSON_PROVIDER_USER'
  | 'ROLE_LESSON_LOCATION_USER'
  | 'ROLE_OPERATOR_USER';

const CreateUser = () => {
  const classes = useStyles();
  const notifications = useSnackbar();
  const history = useHistory();

  const account = useSelector(
    (selector: {
      user: {
        account: User;
      };
    }) => selector.user.account,
  );
  const isSuperAdmin = account.roles.includes('ROLE_SUPER_ADMIN');
  const isAdmin = account.roles.includes('ROLE_ADMIN');
  const isSLPManager = account.roles.includes(
    'ROLE_SWIMMING_LESSON_PROVIDER_MANAGER',
  );

  const [userDto, setUserDto] = useState<UserDTO>({
    id: null,
    email: '',
    role: 'ROLE_ADMIN',
    swimmingLessonProviderId: isSLPManager
      ? account.swimmingLessonProvider!.id
      : null,
    lessonLocationIds: [],
    firstName: '',
    insertion: null,
    lastName: '',
    operatorId: null,
  });
  const [userRole, setUserRole] = useState<SELECTABLE_ROLE>('ROLE_ADMIN');

  const [swimmingLessonProvider, setSwimmingLessonProvider] =
    useState<SwimmingLessonProviderWithLessonLocations | null>(
      account.swimmingLessonProvider || null,
    );
  const [selectedLessonLocations, setSelectedLessonLocations] = useState<
    MinimalLessonLocation[]
  >(() =>
    swimmingLessonProvider && swimmingLessonProvider.lessonLocations
      ? swimmingLessonProvider.lessonLocations
      : [],
  );

  const [violations, setViolations] = useState<ValidationViolation[]>([]);

  const selectOperator = (operator: Operator | null) => {
    setUserDto((prev) => ({
      ...prev,
      operatorId: operator ? operator.id : null,
    }));
  };

  const handleRoleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newRole = event.target.value as SELECTABLE_ROLE;
    setUserRole(newRole);
    setSwimmingLessonProvider(account.swimmingLessonProvider || null);
    setSelectedLessonLocations([]);
    setUserDto((prev) => ({
      ...prev,
      role: newRole,
      swimmingLessonProviderId: account.swimmingLessonProvider?.id || null,
      lessonLocationIds: [],
      operatorId: null,
    }));
  };

  const handleChange = (
    event: React.ChangeEvent<{ name?: string; value: unknown }>,
  ) => {
    setUserDto((prev) => ({
      ...prev,
      [event.target.name as string]: event.target.value,
    }));
  };

  const handleLessonLocationsChange = (
    _event: any,
    updatedLessonLocations: MinimalLessonLocation[],
  ) => {
    setSelectedLessonLocations(updatedLessonLocations);
    setUserDto((prev) => ({
      ...prev,
      lessonLocationIds: updatedLessonLocations.map(
        (lessonLocation) => lessonLocation.id,
      ),
    }));
  };

  const handleMayInvoiceChange = (
    event: React.ChangeEvent<{ name?: string; checked: boolean }>,
  ) => {
    setUserDto((prev) => {
      if (!prev.delegateProfile) {
        return prev;
      }

      return {
        ...prev,
        delegateProfile: {
          ...prev.delegateProfile,
          mayInvoice: event.target.checked,
        },
      };
    });
  };

  const handleMaxDistanceChange = (
    event: React.ChangeEvent<{ name?: string; value: unknown }>,
  ) => {
    const value = parseFloat(`${event.target.value}`.replace(/[^\d.-]/g, ''));

    setUserDto((prev) => {
      if (!prev.delegateProfile) {
        return prev;
      }

      return {
        ...prev,
        delegateProfile: {
          ...prev.delegateProfile,
          maxDistanceExamsInKm: value as number,
        },
      };
    });
  };

  const handleSubmit = () => {
    new UserRepository()
      .create(userDto)
      .then(() => {
        history.push(USER_BASE_ROUTE);
        notifications.enqueueSnackbar('Gebruiker is succesvol aangemaakt!', {
          variant: 'success',
        });
      })
      .catch((error) => {
        if (isValidationErrorResponse(error)) {
          const { violations: newViolations } = error.response.data;
          setViolations(newViolations);
        } else {
          notifications.enqueueSnackbar(
            'Er is iets fout gegaan bij het aanmaken van de gebruiker.',
            { variant: 'error' },
          );
        }
      });
  };

  useEffect(() => {
    setUserDto((prev) => ({
      ...prev,
      swimmingLessonProviderId: swimmingLessonProvider
        ? swimmingLessonProvider.id
        : null,
      lessonLocationIds: [],
    }));
  }, [swimmingLessonProvider]);

  let mayInvoiceCheckbox = null;
  if (userDto.delegateProfile) {
    mayInvoiceCheckbox = (
      <Checkbox
        name="mayInvoice"
        checked={userDto.delegateProfile.mayInvoice}
        onChange={handleMayInvoiceChange}
      />
    );
  }

  return (
    <Page title="Gebruiker aanmaken" breadcrumbs>
      <FormSection title="Gegevens">
        <>
          {!userDto.id && (
            <>
              <FormControl>
                <FormLabel>Rol</FormLabel>
                <RadioGroup
                  value={userRole}
                  name="action"
                  onChange={handleRoleChange}
                >
                  {isSuperAdmin ? (
                    <FormControlLabel
                      control={<Radio />}
                      label="Admin"
                      value="ROLE_ADMIN"
                    />
                  ) : null}
                  <FormControlLabel
                    control={<Radio />}
                    label="Zwemlesaanbiedergebruiker"
                    value="ROLE_SWIMMING_LESSON_PROVIDER_USER"
                  />
                  <FormControlLabel
                    control={<Radio />}
                    label="Leslocatiemedewerker"
                    value="ROLE_LESSON_LOCATION_USER"
                  />
                  {isSuperAdmin && (
                    <FormControlLabel
                      control={<Radio />}
                      label="Exploitant (gebruiker)"
                      value="ROLE_OPERATOR_USER"
                    />
                  )}
                </RadioGroup>
              </FormControl>
              {isAdmin && userRole === 'ROLE_SWIMMING_LESSON_PROVIDER_USER' && (
                <SwimmingLessonProviderSelect
                  onSelect={setSwimmingLessonProvider}
                />
              )}
              {userRole === 'ROLE_LESSON_LOCATION_USER' && (
                <>
                  {isAdmin ? (
                    <SwimmingLessonProviderSelect
                      onSelect={setSwimmingLessonProvider}
                      type="regular_swimming_lesson_provider"
                    />
                  ) : null}
                  {swimmingLessonProvider && (
                    <Box>
                      <FormControl fullWidth>
                        <FormLabel>Leslocatie(s)*</FormLabel>
                        <Box mt={2}>
                          <Autocomplete
                            multiple
                            onChange={handleLessonLocationsChange}
                            value={selectedLessonLocations}
                            options={swimmingLessonProvider.lessonLocations}
                            getOptionLabel={(option) => option.name}
                            fullWidth
                            renderInput={(params) => (
                              <TextField
                                /* eslint-disable-next-line react/jsx-props-no-spreading */
                                {...params}
                                fullWidth
                                placeholder="Selecteer minimaal één leslocatie"
                                error={hasViolationForField(
                                  'lessonLocationIds',
                                  violations,
                                )}
                              />
                            )}
                          />
                        </Box>
                      </FormControl>
                    </Box>
                  )}
                </>
              )}
              {userRole === 'ROLE_OPERATOR_USER' && (
                <OperatorSelect onSelect={selectOperator} />
              )}
            </>
          )}
        </>
        <TextField
          label="E-mail"
          className={classes.field}
          value={userDto.email}
          name="email"
          error={hasViolationForField('email', violations)}
          onChange={handleChange}
          fullWidth
          required
        />
        <TextField
          label="Voornaam"
          className={classes.field}
          value={userDto.firstName}
          name="firstName"
          error={hasViolationForField('firstName', violations)}
          onChange={handleChange}
          fullWidth
          required
        />
        <TextField
          label="Tussenvoegsel"
          className={classes.field}
          value={userDto.insertion}
          name="insertion"
          onChange={handleChange}
          fullWidth
        />
        <TextField
          label="Achternaam"
          className={classes.field}
          value={userDto.lastName}
          name="lastName"
          error={hasViolationForField('lastName', violations)}
          onChange={handleChange}
          fullWidth
          required
        />
      </FormSection>
      {userDto.delegateProfile && isAdmin && (
        <FormSection title="Gedelegeerdenprofiel">
          <>
            {mayInvoiceCheckbox && (
              <FormGroup>
                <FormControlLabel
                  control={mayInvoiceCheckbox}
                  label="Mag factureren"
                />
              </FormGroup>
            )}
          </>
          <TextField
            type="number"
            label="Maximale afstand tot examens in km"
            className={classes.field}
            value={userDto.delegateProfile.maxDistanceExamsInKm}
            name="maxDistanceToExams"
            onChange={handleMaxDistanceChange}
            fullWidth
          />
        </FormSection>
      )}
      <ButtonRow>
        <Button
          variant="contained"
          type="submit"
          color="primary"
          startIcon={<FontAwesomeIcon icon={['fad', 'save']} />}
          onClick={handleSubmit}
        >
          Opslaan
        </Button>
      </ButtonRow>
    </Page>
  );
};

export default CreateUser;
